import React, { useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';
import { Goal, SubGoal, Todo, goalService } from '../services/goalService';
import { useTranslation } from '../i18n';
import { useToast } from '../contexts/ToastContext';
import { useTimers } from '../contexts/TimerContext';
import { formatDate, formatDurationShort } from '../utils/format';
import { todayKey } from '../utils/zonedTime';
import { formatClock, elapsedSecondsSince } from '../utils/timer';
import { shiftReminder } from '../components/HomeToday/HomeToday';
import { useTreeTimerSync } from '../components/HomeContinue/HomeContinue';
import { GoalCardsSkeleton } from '../components/Skeleton/Skeleton';
import Icon from '../components/Icon';
import { Check, EmptyNote, Panel, PanelHead, ProgressLine, Segmented, TaskRow } from '../components/ui';
import {
  TimelineBlock,
  TodayItem,
  buildTodayPlan,
  clockLabel,
  instantAt,
  minutesOfDay,
  snapQuarter,
  timeOnDay,
  timelineBlocks,
  timelineHours,
  weekByGoal,
} from './Today/todayPlan';
import './HomePage.css';

interface HomePageProps {
  goals: Goal[];
  loading?: boolean;
  onUpdateGoal: (id: string, data: Partial<Goal>) => void;
  onToggleFavorite: (id: string) => void;
  onAddSubGoal: (goalId: string, title: string, description: string) => void;
  onUpdateSubGoal: (goalId: string, subGoalId: string, data: Partial<SubGoal>) => void;
  onDeleteSubGoal: (goalId: string, subGoalId: string) => void;
  onUpdateTodo?: (subGoalId: string, todoId: string, data: Partial<Todo>) => Promise<void> | void;
  onNavigateToTasks?: () => void;
  onShowAIModal?: () => void;
  onShowForm?: () => void;
  onShowCreationModal?: () => void;
  /** Reloads the goal tree (after a timer, a completion or a new task). */
  onTimerChanged?: () => void;
}

type TodayView = 'list' | 'timeline';
const VIEW_KEY = 'riselio:todayView';
const LAST_STAGE_KEY = 'riselio:todayStage';
/** Pixels per hour on the timeline (T-181: 48px). */
const HOUR = 48;

const readPref = (key: string): string | null => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};
const writePref = (key: string, value: string) => {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* private mode: the choice lasts this visit */
  }
};

/** Re-renders every [ms] (the live clocks, the "now" line). */
const useTick = (ms: number) => {
  const [, set] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => set((n) => n + 1), ms);
    return () => window.clearInterval(id);
  }, [ms]);
};

const openTodo = (item: TodayItem) => {
  window.location.hash = `#/subgoals/${item.subGoal.id}/todos/${item.todo.id}`;
};

/**
 * Bugün (Faz 23, T-181; w-bugun.html): the date and title with the List / Zaman çizgisi switch
 * and "Görev", a line of counts, then either the hour timeline (07–21, blocks dragged to a
 * new time, the "now" line) or the list (Gecikmiş with "Bugüne al", Bugün with an add row,
 * Tamamlanan). The right rail: Şimdi (the running task), Saatsiz (today's tasks without a
 * time, dragged onto the timeline) and Bu hafta. Continue cards, the motivation banner, the
 * stat tiles and the inspiration screen are gone.
 */
const HomePage: React.FC<HomePageProps> = ({
  goals,
  loading = false,
  onUpdateTodo,
  onShowAIModal,
  onShowForm,
  onShowCreationModal,
  onTimerChanged,
}) => {
  const { t, language } = useTranslation();
  const toast = useToast();
  const { running, stopTimer } = useTimers();
  const deferredGoals = useDeferredValue(goals);
  useTreeTimerSync(goals, running, onTimerChanged);
  const today = todayKey();
  const plan = useMemo(() => buildTodayPlan(deferredGoals, today), [deferredGoals, today]);
  const [view, setView] = useState<TodayView>(() => (readPref(VIEW_KEY) === 'timeline' ? 'timeline' : 'list'));
  const [adding, setAdding] = useState(0);
  const [busy, setBusy] = useState<string | null>(null);

  const changeView = (v: TodayView) => {
    setView(v);
    writePref(VIEW_KEY, v);
  };

  const reload = useCallback(() => onTimerChanged?.(), [onTimerChanged]);

  const toggle = useCallback(async (item: TodayItem) => {
    setBusy(item.todo.id);
    try {
      await goalService.toggleTodoCompletion(item.todo.id);
      reload();
      if (!item.todo.completed) {
        toast.success(t('todos.toast.completed'), {
          actions: [{
            label: t('common.undo'),
            onClick: () => { void goalService.toggleTodoCompletion(item.todo.id).then(reload).catch(() => undefined); },
          }],
        });
      }
    } catch {
      toast.error(t('errors.toggleTodo'));
    } finally {
      setBusy(null);
    }
  }, [reload, t, toast]);

  const schedule = useCallback(async (item: TodayItem, minutes: number) => {
    if (!onUpdateTodo) return;
    const at = snapQuarter(minutes);
    try {
      await onUpdateTodo(item.subGoal.id, item.todo.id, { dueDate: today, reminderAt: instantAt(today, at) });
      toast.success(t('bugun.timeSaved', { time: clockLabel(at) }));
    } catch {
      /* the save showed its error */
    }
  }, [onUpdateTodo, t, toast, today]);

  const moveToToday = useCallback(async (item: TodayItem) => {
    if (!onUpdateTodo) return;
    try {
      await onUpdateTodo(item.subGoal.id, item.todo.id, { dueDate: today, reminderAt: shiftReminder(item.todo.reminderAt, today) });
    } catch {
      /* the save showed its error */
    }
  }, [onUpdateTodo, today]);

  const addTask = useCallback(async (subGoalId: string, title: string) => {
    try {
      const todo = await goalService.createTodo({ subGoalId, title });
      await goalService.updateTodo(subGoalId, todo.id, { dueDate: today });
      writePref(LAST_STAGE_KEY, subGoalId);
      reload();
    } catch {
      toast.error(t('errors.addTodo'));
    }
  }, [reload, t, toast, today]);

  const stop = useCallback(async (item: TodayItem) => {
    setBusy(item.todo.id);
    try {
      await stopTimer(item.todo.id, { startedAt: item.todo.startedAt, onCorrected: reload });
      reload();
    } finally {
      setBusy(null);
    }
  }, [reload, stopTimer]);

  const hasNoGoals = deferredGoals.length === 0;
  if (loading && hasNoGoals) return <GoalCardsSkeleton count={3} />;

  const stages = deferredGoals.flatMap((g) =>
    g.completed ? [] : (g.subGoals ?? []).filter((s) => !s.completed).map((s) => ({ goal: g, stage: s }))
  );
  const runningItem = plan.today.find((i) => i.todo.isStarted && i.todo.startedAt) ?? null;
  const dateLine = formatDate(today, language, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

  const stats: Array<{ key: string; text: string; late?: boolean }> = [
    { key: 'tasks', text: t('bugun.statTasks', { count: plan.today.length }) },
    ...(plan.overdue.length > 0 ? [{ key: 'late', text: t('bugun.statLate', { count: plan.overdue.length }), late: true }] : []),
    { key: 'done', text: t('bugun.statDone', { count: plan.completed.length }) },
    ...(plan.plannedSeconds > 0
      ? [{ key: 'planned', text: t('bugun.statPlanned', { time: formatDurationShort(plan.plannedSeconds, language) }) }]
      : []),
  ];

  return (
    <div className="bugun">
      <div className="bugun__head">
        <div className="bugun__title">
          <span className="bugun__date">
            {dateLine}
            {/* A phone: the numbers join the date, as the app (T-196) */}
            {stats.map((st) => (
              <span key={st.key} className={`ui-phone-stat${st.late ? ' is-late' : ''}`}> · {st.text}</span>
            ))}
          </span>
          <h1>{t('bugun.title')}</h1>
        </div>
        <div className="bugun__actions">
          <Segmented<TodayView>
            label={t('bugun.viewLabel')}
            value={view}
            onChange={changeView}
            options={[
              { value: 'list', label: t('bugun.list') },
              { value: 'timeline', label: t('bugun.timeline') },
            ]}
          />
          <button
            type="button"
            className="ui-btn ui-btn--primary"
            onClick={() => {
              if (hasNoGoals) (onShowCreationModal || onShowForm)?.();
              else {
                changeView('list');
                setAdding((n) => n + 1);
              }
            }}
          >
            <Icon name="plus" /> {t('bugun.newTask')}
          </button>
        </div>
      </div>

      <div className="bugun__stats ui-wide-stats">
        {stats.map((st) => <span key={st.key} className={st.late ? 'is-late' : undefined}>{st.text}</span>)}
      </div>


      {hasNoGoals ? (
        <Panel>
          <EmptyNote
            title={t('home.empty.title')}
            description={t('home.empty.description')}
            action={
              <div className="bugun__empty-actions">
                <button type="button" className="ui-btn ui-btn--primary" onClick={onShowAIModal}>
                  <Icon name="sparkles" /> {t('home.empty.ctaAI')}
                </button>
                <button type="button" className="ui-btn" onClick={onShowForm}>
                  {t('home.empty.ctaManual')}
                </button>
              </div>
            }
          />
        </Panel>
      ) : (
        <div className="bugun__cols">
          <div className="bugun__main">
            {view === 'timeline' ? (
              <TodayTimeline plan={plan} today={today} onSchedule={schedule} />
            ) : (
              <TodayList
                plan={plan}
                today={today}
                stages={stages}
                busy={busy}
                addSignal={adding}
                onToggle={toggle}
                onMoveToToday={moveToToday}
                onAdd={addTask}
                onStop={(item) => { void stop(item); }}
              />
            )}
          </div>
          <aside className={`bugun__rail is-${view}`} aria-label={t('bugun.title')}>
            <NowPanel item={runningItem} busy={busy !== null} onStop={stop} onFinish={toggle} />
            {plan.unscheduled.length > 0 && <Unscheduled items={plan.unscheduled} onSchedule={schedule} />}
            <ThisWeek goals={deferredGoals} today={today} />
          </aside>
        </div>
      )}
    </div>
  );
};

/** A list row's meta: the time and the estimate. */
const rowMeta = (item: TodayItem, today: string, language: string): string => {
  const at = timeOnDay(item.todo, today);
  return [
    at === null ? null : clockLabel(at),
    item.todo.estimatedSeconds ? formatDurationShort(item.todo.estimatedSeconds, language) : null,
  ].filter(Boolean).join(' · ');
};

const RunningClock: React.FC<{ startedAt: string; className?: string }> = ({ startedAt, className }) => {
  useTick(1000);
  return <span className={className}>{formatClock(elapsedSecondsSince(startedAt))}</span>;
};

interface TodayListProps {
  plan: ReturnType<typeof buildTodayPlan>;
  today: string;
  stages: Array<{ goal: Goal; stage: SubGoal }>;
  busy: string | null;
  /** Bumped by "Görev": opens and focuses the add row. */
  addSignal: number;
  onToggle: (item: TodayItem) => void;
  onMoveToToday: (item: TodayItem) => void;
  onAdd: (subGoalId: string, title: string) => void;
  /** Pauses the running task from its row (the app's Şimdi row, T-197). */
  onStop: (item: TodayItem) => void;
}

const TodayList: React.FC<TodayListProps> = ({ plan, today, stages, busy, addSignal, onToggle, onMoveToToday, onAdd, onStop }) => {
  const { t, language } = useTranslation();
  const check = (item: TodayItem) => (
    <Check
      checked={Boolean(item.todo.completed)}
      label={item.todo.completed ? `${t('todos.item.buttons.undo')}: ${item.todo.title}` : `${t('todos.item.buttons.complete')}: ${item.todo.title}`}
      onChange={busy ? undefined : () => onToggle(item)}
    />
  );
  return (
    <div className="bugun__list">
      {plan.overdue.length > 0 && (
        <Panel>
          <PanelHead title={t('bugun.late')} trailing={plan.overdue.length} />
          {plan.overdue.map((item) => (
            <TaskRow
              key={item.todo.id}
              leading={check(item)}
              title={<bdi>{item.todo.title}</bdi>}
              meta={formatDate(item.todo.dueDate as string, language, { day: 'numeric', month: 'short' })}
              metaTone="late"
              onClick={() => openTodo(item)}
              trailing={
                <button type="button" className="ui-btn ui-btn--small" onClick={() => onMoveToToday(item)}>
                  {t('bugun.moveToToday')}
                </button>
              }
            />
          ))}
        </Panel>
      )}
      <Panel>
        <PanelHead title={t('bugun.title')} trailing={t('bugun.statTasks', { count: plan.today.length })} />
        {plan.today.length === 0 && <p className="bugun__note">{t('bugun.emptyToday')}</p>}
        {plan.today.map((item) => {
          const live = Boolean(item.todo.isStarted && item.todo.startedAt);
          return (
            <TaskRow
              key={item.todo.id}
              leading={check(item)}
              title={<bdi>{item.todo.title}</bdi>}
              running={live}
              meta={live ? undefined : rowMeta(item, today, language)}
              onClick={() => openTodo(item)}
              trailing={
                <>
                  <bdi className="bugun__goal-name">{item.goal.title}</bdi>
                  {live && <RunningClock startedAt={item.todo.startedAt as string} className="bugun__live" />}
                  {live && (
                    <button
                      type="button"
                      className="bugun__pause"
                      aria-label={`${t('gorev.pause')}: ${item.todo.title}`}
                      disabled={busy !== null}
                      onClick={(e) => { e.stopPropagation(); onStop(item); }}
                    >
                      <Icon name="pause" aria-hidden="true" />
                    </button>
                  )}
                </>
              }
            />
          );
        })}
        <TodayAddRow stages={stages} signal={addSignal} onAdd={onAdd} />
      </Panel>
      {plan.completed.length > 0 && (
        <Panel>
          <PanelHead title={t('bugun.completed')} trailing={plan.completed.length} />
          {plan.completed.map((item) => {
            const at = minutesOfDay(item.todo.completedAt);
            return (
              <TaskRow
                key={item.todo.id}
                leading={check(item)}
                title={<bdi>{item.todo.title}</bdi>}
                done
                meta={at === null ? undefined : clockLabel(at)}
                onClick={() => openTodo(item)}
              />
            );
          })}
        </Panel>
      )}
    </div>
  );
};

/** "+ Görev ekle" with the stage it goes into (the last one used first). */
const TodayAddRow: React.FC<{
  stages: Array<{ goal: Goal; stage: SubGoal }>;
  signal: number;
  onAdd: (subGoalId: string, title: string) => void;
}> = ({ stages, signal, onAdd }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [stageId, setStageId] = useState<string>(() => {
    const last = readPref(LAST_STAGE_KEY);
    return stages.find((s) => s.stage.id === last)?.stage.id ?? stages[0]?.stage.id ?? '';
  });
  const input = useRef<HTMLInputElement>(null);
  const focusSoon = () => window.setTimeout(() => input.current?.focus(), 0);

  useEffect(() => {
    if (signal > 0) {
      setOpen(true);
      focusSoon();
    }
  }, [signal]);

  useEffect(() => {
    if (!stages.some((s) => s.stage.id === stageId) && stages[0]) setStageId(stages[0].stage.id);
  }, [stages, stageId]);

  if (stages.length === 0) return <p className="bugun__note">{t('bugun.noStage')}</p>;
  if (!open) {
    return (
      <button type="button" className="ui-add-row" onClick={() => { setOpen(true); focusSoon(); }}>
        <span className="ui-add-row__plus" aria-hidden="true"><Icon name="plus" /></span>
        {t('bugun.add')}
      </button>
    );
  }
  return (
    <div className="ui-add-input bugun__add">
      <input
        ref={input}
        type="text"
        aria-label={t('bugun.add')}
        placeholder={t('bugun.addHint')}
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            setTitle('');
            setOpen(false);
          } else if (e.key === 'Enter') {
            e.preventDefault();
            const text = title.trim();
            if (!text || !stageId) return;
            onAdd(stageId, text);
            setTitle('');
          }
        }}
      />
      <select aria-label={t('bugun.addTo')} value={stageId} onChange={(e) => setStageId(e.target.value)}>
        {stages.map(({ goal, stage }) => (
          <option key={stage.id} value={stage.id}>{`${goal.title} › ${stage.title}`}</option>
        ))}
      </select>
    </div>
  );
};

/** The hour timeline: blocks by start time; drag (or Alt+↑/↓) to move one by a quarter hour. */
const TodayTimeline: React.FC<{
  plan: ReturnType<typeof buildTodayPlan>;
  today: string;
  onSchedule: (item: TodayItem, minutes: number) => void;
}> = ({ plan, today, onSchedule }) => {
  const { t, language } = useTranslation();
  useTick(60000);
  const now = new Date();
  const blocks = timelineBlocks(plan, today, now);
  const { from, to } = timelineHours(blocks);
  const area = useRef<HTMLDivElement>(null);
  const [drag, setDrag] = useState<{ id: string; start: number; y: number; at: number } | null>(null);
  const nowMinutes = minutesOfDay(now.toISOString()) ?? 0;
  const top = (minutes: number) => ((minutes - from * 60) / 60) * HOUR;
  const minutesAtY = (clientY: number) => {
    const box = area.current?.getBoundingClientRect();
    return box ? from * 60 + ((clientY - box.top) / HOUR) * 60 : 0;
  };
  const startOf = (b: TimelineBlock) => (drag?.id === b.item.todo.id ? drag.at : b.start);

  return (
    <Panel className="bugun__timeline-panel">
      <div
        ref={area}
        className="bugun-timeline"
        role="list"
        aria-label={t('bugun.timelineLabel')}
        style={{ height: (to - from + 1) * HOUR }}
        onDragOver={(e) => {
          if (Array.from(e.dataTransfer.types).includes('text/riselio-todo')) e.preventDefault();
        }}
        onDrop={(e) => {
          const id = e.dataTransfer.getData('text/riselio-todo');
          const item = plan.unscheduled.find((i) => i.todo.id === id);
          if (item) {
            e.preventDefault();
            onSchedule(item, minutesAtY(e.clientY));
          }
        }}
      >
        {Array.from({ length: to - from + 1 }, (_, i) => from + i).map((h) => (
          <div key={h} className="bugun-timeline__hour" style={{ top: (h - from) * HOUR }} aria-hidden="true">
            {/* The "now" label takes the place of an hour label right next to it */}
            <span>{Math.abs(nowMinutes - h * 60) < 12 ? '' : clockLabel(h * 60)}</span>
          </div>
        ))}
        {blocks.map((b) => {
          const start = startOf(b);
          const movable = !b.done && !b.running;
          const classes = [
            'bugun-block',
            b.running && 'is-live',
            b.done && 'is-done',
            b.length < 40 && 'is-short',
            drag?.id === b.item.todo.id && 'is-dragging',
          ].filter(Boolean).join(' ');
          return (
            <div
              key={b.item.todo.id}
              role="listitem"
              tabIndex={0}
              aria-label={t('bugun.blockHint', { title: b.item.todo.title, time: clockLabel(start) })}
              className={classes}
              style={{ top: top(start), height: Math.max(22, (b.length / 60) * HOUR - 2) }}
              onPointerDown={(e) => {
                if (!movable || e.button !== 0) return;
                e.currentTarget.setPointerCapture?.(e.pointerId);
                setDrag({ id: b.item.todo.id, start: b.start, y: e.clientY, at: b.start });
              }}
              onPointerMove={(e) => {
                if (drag?.id !== b.item.todo.id) return;
                setDrag({ ...drag, at: snapQuarter(drag.start + ((e.clientY - drag.y) / HOUR) * 60) });
              }}
              onPointerUp={() => {
                if (drag?.id !== b.item.todo.id) {
                  if (!movable) openTodo(b.item);
                  return;
                }
                const moved = drag.at !== drag.start;
                setDrag(null);
                if (moved) onSchedule(b.item, drag.at);
                else openTodo(b.item);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') openTodo(b.item);
                if (movable && e.altKey && (e.key === 'ArrowUp' || e.key === 'ArrowDown')) {
                  e.preventDefault();
                  onSchedule(b.item, b.start + (e.key === 'ArrowUp' ? -15 : 15));
                }
              }}
            >
              <bdi className="bugun-block__title">{b.item.todo.title}</bdi>
              <span className="bugun-block__meta">
                {b.running && b.item.todo.startedAt ? (
                  <>
                    {t('bugun.running')} · <RunningClock startedAt={b.item.todo.startedAt} />
                  </>
                ) : b.done ? (
                  t('bugun.doneShort')
                ) : (
                  [clockLabel(start), b.item.todo.estimatedSeconds ? formatDurationShort(b.item.todo.estimatedSeconds, language) : null, b.item.goal.title]
                    .filter(Boolean)
                    .join(' · ')
                )}
              </span>
            </div>
          );
        })}
        {nowMinutes >= from * 60 && nowMinutes <= (to + 1) * 60 && (
          <div className="bugun-timeline__now" style={{ top: top(nowMinutes) }} aria-hidden="true">
            <span>{clockLabel(nowMinutes)}</span>
          </div>
        )}
      </div>
    </Panel>
  );
};

/** "Şimdi": the running task's clock with Stop and Finish. */
const NowPanel: React.FC<{
  item: TodayItem | null;
  busy: boolean;
  onStop: (item: TodayItem) => void;
  onFinish: (item: TodayItem) => void;
}> = ({ item, busy, onStop, onFinish }) => {
  const { t } = useTranslation();
  return (
    <Panel className="bugun-now">
      <div className="bugun-now__head">
        <span className="bugun-label">{t('bugun.now')}</span>
        {item && (
          <span className="bugun-now__live">
            <span aria-hidden="true" />
            {t('bugun.running')}
          </span>
        )}
      </div>
      {item && item.todo.startedAt ? (
        <>
          <RunningClock startedAt={item.todo.startedAt} className="bugun-now__clock" />
          <button type="button" className="bugun-now__title" onClick={() => openTodo(item)}>
            <bdi>{item.todo.title}</bdi>
          </button>
          <bdi className="bugun-now__path">{`${item.goal.title} › ${item.subGoal.title}`}</bdi>
          <div className="bugun-now__actions">
            <button type="button" className="ui-btn" disabled={busy} onClick={() => onStop(item)}>
              {t('bugun.stop')}
            </button>
            <button type="button" className="ui-btn ui-btn--primary" disabled={busy} onClick={() => onFinish(item)}>
              {t('bugun.finish')}
            </button>
          </div>
        </>
      ) : (
        <p className="bugun__note">{t('bugun.nothingRunning')}</p>
      )}
    </Panel>
  );
};

/** "Saatsiz": today's tasks without a time; drag one onto the timeline or type a time. */
const Unscheduled: React.FC<{ items: TodayItem[]; onSchedule: (item: TodayItem, minutes: number) => void }> = ({
  items,
  onSchedule,
}) => {
  const { t } = useTranslation();
  return (
    <Panel className="bugun-shelf">
      <PanelHead title={t('bugun.unscheduled')} trailing={t('bugun.unscheduledHint')} />
      {items.map((item) => (
        <div
          key={item.todo.id}
          className="bugun-shelf__row"
          draggable
          onDragStart={(e) => {
            e.dataTransfer.setData('text/riselio-todo', item.todo.id);
            e.dataTransfer.effectAllowed = 'move';
          }}
        >
          <Icon name="grip" className="bugun-shelf__grip" aria-hidden="true" />
          <bdi className="bugun-shelf__title">{item.todo.title}</bdi>
          <input
            type="time"
            step={900}
            aria-label={t('bugun.setTime', { title: item.todo.title })}
            onChange={(e) => {
              const [h, m] = e.target.value.split(':').map(Number);
              if (Number.isFinite(h) && Number.isFinite(m)) onSchedule(item, h * 60 + m);
            }}
          />
        </div>
      ))}
    </Panel>
  );
};

/** "Bu hafta": this week's tasks per goal. */
const ThisWeek: React.FC<{ goals: Goal[]; today: string }> = ({ goals, today }) => {
  const { t } = useTranslation();
  const rows = weekByGoal(goals, today);
  if (rows.length === 0) return null;
  return (
    <Panel className="bugun-weekpanel">
      <PanelHead title={t('bugun.thisWeek')} trailing={<a href="#/progress">{t('navigation.progress')}</a>} />
      <div className="bugun-week">
        {rows.map((r) => (
          <a key={r.goal.id} href={`#/goals/${r.goal.id}`} className="bugun-week__row">
            <span className="bugun-week__line">
              <bdi>{r.goal.title}</bdi>
              <strong>{`${r.done}/${r.total}`}</strong>
            </span>
            <ProgressLine value={r.done / r.total} />
          </a>
        ))}
      </div>
    </Panel>
  );
};

export default React.memo(HomePage);
